import { useState, useMemo, useEffect, useCallback, ReactNode, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { AxiosError } from 'axios';

import { ColorModeToggle } from 'components/ColorModeToggle';
import { joinWaitlist, getWaitlistCount } from '../api';

import { usePersona } from '../usePersona';
import {
  FEATURED_TESTIMONIAL_NAMES,
  TESTIMONIALS,
  TESTIMONIALS_DISPLAYED,
} from './constants';
import { waitlistSchema, WaitlistFormValues } from './schema';
import * as styled from './styles';
import { CtaBanner } from './ui/cta-banner';
import { EverythingBento } from './ui/everything-bento';
import { Hero } from './ui/hero';
import { PersonaGate } from './ui/persona-gate';
import { SiteFooter } from './ui/site-footer';
import { StorySection } from './ui/story-section';
import { TestimonialsSection } from './ui/testimonials-section';

export const LandingScene = () => {
  const [waitlistCount, setWaitlistCount] = useState<number | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { persona, hasPicked, selectPersona } = usePersona();
  const [gateOpen, setGateOpen] = useState(false);

  const handleGateSelect = (id: typeof persona.id) => {
    selectPersona(id);
    setGateOpen(false);
  };

  const form = useForm<WaitlistFormValues>({
    resolver: zodResolver(waitlistSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      email: '',
      phone: undefined,
    },
  });

  useEffect(() => {
    getWaitlistCount()
      .then((response) => setWaitlistCount(response.data.count))
      .catch(() => {});
  }, []);

  const personaKnown = hasPicked && persona.id !== 'other';

  type WaitlistError = AxiosError<{
    errors?: Record<string, string[]>;
    error?: string;
  }>;

  const handleJoinWaitlist = (values: WaitlistFormValues) => {
    setFormError(null);
    return joinWaitlist({
      name: values.name.trim(),
      email: values.email.trim(),
      phone:
        values.phone && isValidPhoneNumber(values.phone)
          ? values.phone
          : undefined,
      profession: personaKnown ? persona.label : undefined,
    })
      .then((response) => {
        setFormSuccess(true);
        setWaitlistCount(response.data.position);
      })
      .catch((error: WaitlistError) => {
        const emailError = error.response?.data?.errors?.email?.[0];
        if (emailError) setFormError(emailError);
      });
  };

  const handleWaitlistScroll = useCallback(() => {
    document
      .getElementById('waitlist')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const visibleTestimonials = useMemo(() => {
    const byName = new Map(
      TESTIMONIALS.map((testimonial) => [testimonial.name, testimonial]),
    );
    const featured = FEATURED_TESTIMONIAL_NAMES.map((name) =>
      byName.get(name),
    ).filter(
      (testimonial): testimonial is Landing.Testimonial => !!testimonial,
    );
    return featured.slice(0, TESTIMONIALS_DISPLAYED);
  }, []);

  return (
    <div>
      <styled.AnnouncementBar>
        Build your reputation with{' '}
        <styled.AnnouncementAccent>verified reviews</styled.AnnouncementAccent>,
        your <styled.AnnouncementAccent>portfolio</styled.AnnouncementAccent>,
        and <styled.AnnouncementAccent>real referrals</styled.AnnouncementAccent>{' '}
        — then take it anywhere.
      </styled.AnnouncementBar>
      <styled.Nav>
        <styled.NavLogo>Kudable</styled.NavLogo>
        <styled.NavRight>
          <styled.PersonaChip onClick={() => setGateOpen(true)} type="button">
            {hasPicked
              ? `I'm a ${persona.label} · Change`
              : 'What kind of pro are you?'}
          </styled.PersonaChip>
          <ColorModeToggle />
        </styled.NavRight>
      </styled.Nav>

      <Hero
        bucket={persona.bucket}
        form={form}
        formSuccess={formSuccess}
        formError={formError}
        onSubmit={handleJoinWaitlist}
      />

      {(() => {
        const sections: ReactNode[] = [
          <StorySection
            key="story"
            personaId={persona.id}
            onWaitlistClick={handleWaitlistScroll}
          />,
        ];
        sections.push(<EverythingBento key="bento" />);
        sections.push(
          <TestimonialsSection
            key="testimonials"
            testimonials={visibleTestimonials}
          />,
        );
        return sections.map((node, index) => (
          <styled.SectionBand
            key={(node as ReactElement).key ?? index}
            $variant={index % 2 === 0 ? 'grey' : 'white'}
          >
            {node}
          </styled.SectionBand>
        ));
      })()}

      <CtaBanner waitlistCount={waitlistCount} />

      <SiteFooter />

      {gateOpen && (
        <PersonaGate
          onSelect={handleGateSelect}
          onClose={() => setGateOpen(false)}
        />
      )}
    </div>
  );
};
